import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Box,
  Avatar,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: "center",
    flexWrap: "wrap",
  },
  addButton: {
    marginLeft: "auto",
  },
  tableContainer: {
    maxHeight: 600,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  inactiveChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  dialogContent: {
    minWidth: 500,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  productInfo: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  productOption: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },
  productImage: {
    width: 60,
    height: 60,
    objectFit: "cover",
    borderRadius: 4,
    border: "1px solid #e0e0e0",
  },
  productDetails: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  productName: {
    fontWeight: 500,
    fontSize: "0.9rem",
    marginBottom: 2,
  },
  productMeta: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  productPrice: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: "0.9rem",
  },
});

const hebrewLabels = {
  title: "ניהול קופונים",
  filters: {
    code: "קוד קופון",
    ean: "EAN",
    active: "פעיל",
    all: "הכל",
    search: "חיפוש",
  },
  table: {
    code: "קוד",
    ean: "EAN",
    product: "מוצר",
    discount: "הנחה (₪)",
    active: "סטטוס",
    actions: "פעולות",
  },
  buttons: {
    add: "הוסף קופון חדש",
    edit: "עריכה",
    delete: "מחיקה",
    save: "שמור",
    cancel: "ביטול",
    toggleActive: "שנה סטטוס",
  },
  dialog: {
    create: "יצירת קופון חדש",
    edit: "עריכת קופון",
    delete: "מחיקת קופון",
    deleteConfirm: "האם אתה בטוח שברצונך למחוק קופון זה?",
  },
  messages: {
    loading: "טוען...",
    noData: "לא נמצאו קופונים",
    success: "הפעולה בוצעה בהצלחה",
    error: "אירעה שגיאה",
    loadingProducts: "טוען מוצרים...",
  },
  fields: {
    code: "קוד קופון (באנגלית, אותיות גדולות)",
    ean: "בחר מוצר",
    discount: "סכום הנחה (₪)",
    active: "פעיל",
  },
  status: {
    active: "פעיל",
    inactive: "לא פעיל",
  },
};

const basePath = utils.getEdgeBasePath();

class CouponsManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      coupons: [],
      products: [],
      loading: true,
      loadingProducts: false,
      pagination: {
        page: 0,
        limit: 20,
        total: 0,
      },
      filters: {
        code: "",
        ean: "",
        active: "",
      },
      dialogOpen: false,
      dialogMode: "create", // create or edit
      currentCoupon: {
        _id: null,
        code: "",
        ean: "",
        discountAmount: "",
        active: true,
      },
      deleteDialogOpen: false,
      couponToDelete: null,
      snackbar: {
        open: false,
        message: "",
        severity: "success",
      },
    };
  }

  componentDidMount() {
    this.loadCoupons();
    this.loadProducts();
  }

  loadProducts = async () => {
    this.setState({ loadingProducts: true });
    
    try {
      const response = await fetch(`${basePath}/api/v1/admin/products/list`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await response.json();

      if (data.ok) {
        this.setState({ products: data.products });
      }
    } catch (error) {
      console.error("Error loading products:", error);
      this.showSnackbar("שגיאה בטעינת המוצרים", "error");
    } finally {
      this.setState({ loadingProducts: false });
    }
  };

  loadCoupons = async () => {
    this.setState({ loading: true });
    
    try {
      const { pagination, filters } = this.state;
      const queryParams = new URLSearchParams({
        page: pagination.page + 1,
        limit: pagination.limit,
      });

      if (filters.code) queryParams.append("code", filters.code);
      if (filters.ean) queryParams.append("ean", filters.ean);
      if (filters.active) queryParams.append("active", filters.active);

      const response = await fetch(
        `${basePath}/api/v1/admin/coupons?${queryParams.toString()}`,
        { headers: utils.getAdminAuthHeaders() }
      );
      const data = await response.json();

      if (data.ok) {
        this.setState({
          coupons: data.coupons,
          pagination: {
            ...pagination,
            total: data.pagination.total,
          },
        });
      }
    } catch (error) {
      console.error("Error loading coupons:", error);
      this.showSnackbar("שגיאה בטעינת הקופונים", "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  handlePageChange = (event, newPage) => {
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, page: newPage },
      }),
      () => this.loadCoupons()
    );
  };

  handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, limit: newLimit, page: 0 },
      }),
      () => this.loadCoupons()
    );
  };

  handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        [field]: value,
      },
    }));
  };

  handleSearch = () => {
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, page: 0 },
      }),
      () => this.loadCoupons()
    );
  };

  openCreateDialog = () => {
    this.setState({
      dialogOpen: true,
      dialogMode: "create",
      currentCoupon: {
        _id: null,
        code: "",
        ean: "",
        discountAmount: "",
        active: true,
      },
    });
  };

  openEditDialog = (coupon) => {
    this.setState({
      dialogOpen: true,
      dialogMode: "edit",
      currentCoupon: {
        _id: coupon._id,
        code: coupon.code,
        ean: coupon.ean,
        discountAmount: coupon.discountAmount,
        active: coupon.active,
      },
    });
  };

  openDeleteDialog = (coupon) => {
    this.setState({
      deleteDialogOpen: true,
      couponToDelete: coupon,
    });
  };

  closeDialog = () => {
    this.setState({ dialogOpen: false });
  };

  closeDeleteDialog = () => {
    this.setState({ deleteDialogOpen: false, couponToDelete: null });
  };

  handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    
    this.setState((prevState) => ({
      currentCoupon: {
        ...prevState.currentCoupon,
        [field]: value,
      },
    }));
  };

  handleActiveToggle = (event) => {
    const checked = event.target.checked;
    
    this.setState((prevState) => ({
      currentCoupon: {
        ...prevState.currentCoupon,
        active: checked,
      },
    }));
  };

  handleEanChange = (event, value) => {
    this.setState((prevState) => ({
      currentCoupon: {
        ...prevState.currentCoupon,
        ean: value ? value.ean : "",
      },
    }));
  };

  handleSave = async () => {
    const { dialogMode, currentCoupon } = this.state;

    // Validation
    if (!currentCoupon.code || !currentCoupon.ean || !currentCoupon.discountAmount) {
      this.showSnackbar("נא למלא את כל השדות הנדרשים", "error");
      return;
    }

    try {
      const payload = {
        code: currentCoupon.code.toUpperCase(),
        ean: currentCoupon.ean,
        discountAmount: parseInt(currentCoupon.discountAmount),
        active: currentCoupon.active,
      };

      let response;
      if (dialogMode === "create") {
        response = await fetch(`${basePath}/api/v1/admin/coupons`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...utils.getAdminAuthHeaders() },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(
          `${basePath}/api/v1/admin/coupons/${currentCoupon._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json", ...utils.getAdminAuthHeaders() },
            body: JSON.stringify(payload),
          }
        );
      }

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar(
          dialogMode === "create" ? "הקופון נוצר בהצלחה" : "הקופון עודכן בהצלחה",
          "success"
        );
        this.closeDialog();
        this.loadCoupons();
      } else {
        this.showSnackbar(data.message || "שגיאה בשמירת הקופון", "error");
      }
    } catch (error) {
      console.error("Error saving coupon:", error);
      this.showSnackbar("שגיאה בשמירת הקופון", "error");
    }
  };

  handleDelete = async () => {
    const { couponToDelete } = this.state;

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/coupons/${couponToDelete._id}`,
        {
          method: "DELETE",
          headers: utils.getAdminAuthHeaders(),
        }
      );

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar("הקופון נמחק בהצלחה", "success");
        this.closeDeleteDialog();
        this.loadCoupons();
      } else {
        this.showSnackbar(data.message || "שגיאה במחיקת הקופון", "error");
      }
    } catch (error) {
      console.error("Error deleting coupon:", error);
      this.showSnackbar("שגיאה במחיקת הקופון", "error");
    }
  };

  handleToggleActive = async (coupon) => {
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/coupons/${coupon._id}/toggle`,
        {
          method: "PATCH",
          headers: utils.getAdminAuthHeaders(),
        }
      );

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar(
          `הקופון ${data.active ? "הופעל" : "הושבת"} בהצלחה`,
          "success"
        );
        this.loadCoupons();
      } else {
        this.showSnackbar(data.message || "שגיאה בשינוי סטטוס הקופון", "error");
      }
    } catch (error) {
      console.error("Error toggling coupon status:", error);
      this.showSnackbar("שגיאה בשינוי סטטוס הקופון", "error");
    }
  };

  showSnackbar = (message, severity = "success") => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity,
      },
    });
  };

  closeSnackbar = () => {
    this.setState((prevState) => ({
      snackbar: {
        ...prevState.snackbar,
        open: false,
      },
    }));
  };

  getProductByEan = (ean) => {
    return this.state.products.find((p) => p.ean === ean);
  };

  getProductImageUrl = (product) => {
    if (!product || !product.cdn || !Array.isArray(product.cdn) || product.cdn.length === 0) {
      return null;
    }
    
    // Find primary image or use first image
    const primaryImage = product.cdn.find(img => img.primay === true || img.primary === true);
    const imageToUse = primaryImage || product.cdn[0];
    
    if (imageToUse && imageToUse.bucketPath) {
      return `https://s3.eu-west-2.amazonaws.com/sportyshop/${imageToUse.bucketPath}`;
    }
    
    return null;
  };

  formatProductLabel = (product) => {
    if (!product) return '';
    
    const parts = [];
    if (product.displayName) parts.push(product.displayName);
    else if (product.brand && product.name) parts.push(`${product.brand} ${product.name}`);
    
    if (product.displaySize) parts.push(product.displaySize);
    else if (product.size) parts.push(product.size);
    
    if (product.ean) parts.push(`EAN: ${product.ean}`);
    
    return parts.join(' - ');
  };

  render() {
    const { classes } = this.props;
    const {
      coupons,
      products,
      loading,
      loadingProducts,
      pagination,
      filters,
      dialogOpen,
      dialogMode,
      currentCoupon,
      deleteDialogOpen,
      couponToDelete,
      snackbar,
    } = this.state;

    return (
      <div className={classes.root}>
        {/* Filter Bar */}
        <div className={classes.filterBar}>
          <TextField
            label={hebrewLabels.filters.code}
            value={filters.code}
            onChange={this.handleFilterChange("code")}
            variant="outlined"
            size="small"
          />
          <TextField
            label={hebrewLabels.filters.ean}
            value={filters.ean}
            onChange={this.handleFilterChange("ean")}
            variant="outlined"
            size="small"
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
            <InputLabel>{hebrewLabels.filters.active}</InputLabel>
            <Select
              value={filters.active}
              onChange={this.handleFilterChange("active")}
              label={hebrewLabels.filters.active}
            >
              <MenuItem value="">{hebrewLabels.filters.all}</MenuItem>
              <MenuItem value="true">{hebrewLabels.status.active}</MenuItem>
              <MenuItem value="false">{hebrewLabels.status.inactive}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={this.handleSearch}
          >
            {hebrewLabels.filters.search}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={this.openCreateDialog}
            className={classes.addButton}
          >
            {hebrewLabels.buttons.add}
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{hebrewLabels.table.code}</TableCell>
                  <TableCell>{hebrewLabels.table.ean}</TableCell>
                  <TableCell>{hebrewLabels.table.product}</TableCell>
                  <TableCell>{hebrewLabels.table.discount}</TableCell>
                  <TableCell>{hebrewLabels.table.active}</TableCell>
                  <TableCell>{hebrewLabels.table.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">
                        {hebrewLabels.messages.noData}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  coupons.map((coupon) => {
                    const product = this.getProductByEan(coupon.ean);
                    return (
                      <TableRow key={coupon._id}>
                        <TableCell>
                          <strong>{coupon.code}</strong>
                        </TableCell>
                        <TableCell>{coupon.ean}</TableCell>
                        <TableCell>
                          {coupon.scope === "cart" && !coupon.ean ? (
                            <Typography variant="body2">
                              {coupon.discountAmount}% על כל הסל
                            </Typography>
                          ) : product ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              {this.getProductImageUrl(product) ? (
                                <img 
                                  src={this.getProductImageUrl(product)} 
                                  alt={product.displayName || product.name || 'Product'}
                                  style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                                />
                              ) : (
                                <Avatar style={{ width: 40, height: 40 }}>
                                  {(product.brand || product.displayName || product.name || 'P')[0].toUpperCase()}
                                </Avatar>
                              )}
                              <div>
                                <Typography variant="body2" style={{ fontWeight: 500 }}>
                                  {(product.displayName || product.name || product.brand || 'מוצר').trim()}
                                  {(product.displaySize || product.size) && ` ${(product.displaySize || product.size).trim()}`}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  EAN: {product.ean}
                                  {product.price && ` • ₪${product.price}`}
                                </Typography>
                              </div>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              מוצר לא נמצא
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <strong>
                            {coupon.discountType === "percentage"
                              ? `${coupon.discountAmount}%`
                              : `₪${coupon.discountAmount}`}
                          </strong>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              coupon.active
                                ? hebrewLabels.status.active
                                : hebrewLabels.status.inactive
                            }
                            size="small"
                            className={
                              coupon.active
                                ? classes.activeChip
                                : classes.inactiveChip
                            }
                            icon={
                              coupon.active ? (
                                <CheckCircleIcon style={{ color: "white" }} />
                              ) : (
                                <CancelIcon style={{ color: "white" }} />
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => this.handleToggleActive(coupon)}
                            color="primary"
                            title={hebrewLabels.buttons.toggleActive}
                          >
                            {coupon.active ? <CancelIcon /> : <CheckCircleIcon />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => this.openEditDialog(coupon)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => this.openDeleteDialog(coupon)}
                            color="secondary"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={this.handlePageChange}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={this.handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
        />

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={this.closeDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {dialogMode === "create"
              ? hebrewLabels.dialog.create
              : hebrewLabels.dialog.edit}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <TextField
              label={hebrewLabels.fields.code}
              value={currentCoupon.code}
              onChange={this.handleFieldChange("code")}
              fullWidth
              className={classes.formField}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              helperText="הקוד יומר אוטומטית לאותיות גדולות"
            />
            
            <Autocomplete
              options={products}
              getOptionLabel={this.formatProductLabel}
              value={products.find((p) => p.ean === currentCoupon.ean) || null}
              onChange={this.handleEanChange}
              loading={loadingProducts}
              renderOption={(option) => {
                const imageUrl = this.getProductImageUrl(option);
                return (
                  <Box className={classes.productOption}>
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={option.displayName || option.name || 'Product'}
                        className={classes.productImage}
                      />
                    ) : (
                      <Avatar className={classes.productImage}>
                        {(option.brand || option.displayName || option.name || 'P')[0].toUpperCase()}
                      </Avatar>
                    )}
                    <Box className={classes.productDetails}>
                      <Typography className={classes.productName}>
                        {(option.displayName || option.name || option.brand || 'מוצר').trim()}
                        {(option.displaySize || option.size) && ` ${(option.displaySize || option.size).trim()}`}
                      </Typography>
                      <Typography className={classes.productMeta}>
                        EAN: {option.ean}
                        {option.price && ` • ₪${option.price}`}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={hebrewLabels.fields.ean}
                  variant="outlined"
                  className={classes.formField}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingProducts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />

            <TextField
              label={hebrewLabels.fields.discount}
              value={currentCoupon.discountAmount}
              onChange={this.handleFieldChange("discountAmount")}
              fullWidth
              type="number"
              className={classes.formField}
              helperText="סכום ההנחה בשקלים"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={currentCoupon.active}
                  onChange={this.handleActiveToggle}
                  color="primary"
                />
              }
              label={hebrewLabels.fields.active}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog}>{hebrewLabels.buttons.cancel}</Button>
            <Button onClick={this.handleSave} color="primary" variant="contained">
              {hebrewLabels.buttons.save}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={this.closeDeleteDialog}>
          <DialogTitle>{hebrewLabels.dialog.delete}</DialogTitle>
          <DialogContent>
            <Typography>{hebrewLabels.dialog.deleteConfirm}</Typography>
            {couponToDelete && (
              <div style={{ marginTop: 16 }}>
                <Typography variant="body2">
                  <strong>קוד:</strong> {couponToDelete.code}
                </Typography>
                <Typography variant="body2">
                  <strong>EAN:</strong> {couponToDelete.ean}
                </Typography>
                <Typography variant="body2">
                  <strong>הנחה:</strong> ₪{couponToDelete.discountAmount}
                </Typography>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDeleteDialog}>
              {hebrewLabels.buttons.cancel}
            </Button>
            <Button onClick={this.handleDelete} color="secondary" variant="contained">
              {hebrewLabels.buttons.delete}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

CouponsManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CouponsManager);

